/**
 * Thẻ tải logo riêng cho tổ chức. Dùng ở hai nơi:
 *  - Cài đặt → Thông tin công ty (owner/admin tự đặt, API /settings/logo)
 *  - Console platform → chi tiết tổ chức (super admin, API /platform/orgs/:id/logo)
 * Caller truyền hàm gọi API phù hợp; thẻ chỉ lo đọc tệp, xem trước, bấm nút.
 */
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Image as ImageIcon, Loader2, Trash2, Upload } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { urlThuongHieu } from '@/lib/config'

export function docTepThanhDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

export function OrgLogoCard({
  logoUrl, canEdit, onUpload, onClear, dangLuu, moTa,
}: {
  /** Logo riêng hiện tại (null = đang dùng logo nền tảng). */
  logoUrl: string | null | undefined
  canEdit: boolean
  onUpload: (dataUrl: string) => Promise<unknown>
  onClear: () => Promise<unknown>
  dangLuu?: boolean
  moTa?: string
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [broken, setBroken] = useState(false)
  useEffect(() => { setBroken(false) }, [logoUrl])
  const src = logoUrl ? urlThuongHieu(logoUrl) : ''

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.size > 1024 * 1024) { toast.error('Logo tối đa 1 MB'); return }
    try {
      await onUpload(await docTepThanhDataUrl(file))
      toast.success('Đã cập nhật logo tổ chức')
    } catch (err) {
      toast.error((err as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Không tải được logo')
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Logo tổ chức</CardTitle>
        <CardDescription>{moTa ?? 'Hiện ở góc trái thanh menu của mọi người dùng trong tổ chức. Không đặt thì dùng logo nền tảng. PNG/JPG/SVG/WebP, ≤ 1 MB, nền trong suốt nhìn đẹp nhất.'}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex h-24 w-full items-center justify-center overflow-hidden rounded-lg border bg-muted/30">
          {!src || broken ? (
            <div className="flex flex-col items-center text-muted-foreground">
              <ImageIcon className="h-6 w-6" />
              <span className="mt-1 text-xs">{src ? 'Không tải được ảnh' : 'Đang dùng logo nền tảng'}</span>
            </div>
          ) : (
            <img src={src} alt="Logo tổ chức" className="max-h-20 max-w-[70%] object-contain" onError={() => setBroken(true)} />
          )}
        </div>
        <div className="flex gap-2">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={onFile} />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={!canEdit || dangLuu}>
            {dangLuu ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Tải logo lên
          </Button>
          <Button
            variant="ghost" disabled={!canEdit || dangLuu || !logoUrl}
            onClick={async () => { try { await onClear(); toast.success('Đã bỏ logo riêng, dùng logo nền tảng') } catch { toast.error('Không bỏ được logo') } }}
          >
            <Trash2 className="h-4 w-4" /> Dùng logo nền tảng
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
